import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  useIonToast
} from '@ionic/react';
import { Box, Button, TextField, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

interface EditFormData {
  title: string;
  description: string;
  images: string[];
}

export const EditPost: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { userData: currentUser, user: authUser, isLoading } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [formData, setFormData] = useState<EditFormData>({
    title: '',
    description: '',
    images: []
  });
  const [present] = useIonToast();
  
  const presentToast = (message: string, color: 'success' | 'warning' | 'danger') => {
    present({
      message,
      duration: 2000,
      color,
      position: 'bottom'
    });
  };
  const history = useHistory();
  const maxTitleChars = 50;
  const maxDescriptionChars = 10000;

  useEffect(() => {
    const init = async () => {
      setIsPageLoading(true);
      
      try {
        if (isLoading) return;
        
        if (!authUser) {
          history.replace('/auth');
          return;
        }

        if (!id) {
          history.replace('/home');
          return;
        }

        await loadPost();
      } finally {
        setIsPageLoading(false);
      }
    };

    init();
  }, [id, authUser, isLoading, history, t]);

  const loadPost = async () => {
    if (!authUser?.uid) {
      history.replace('/home');
      return;
    }
    try {
      const postDoc = await getDoc(doc(db, 'posts', id));
      if (!postDoc.exists()) {
        presentToast(t('edit_post.not_found'), 'danger');
        history.goBack();
        return;
      }

      const postData = postDoc.data();
      
      // Verify ownership
      if (postData.authorId !== authUser?.uid) {
        presentToast(t('edit_post.unauthorized'), 'danger');
        history.goBack();
        return;
      }

      setFormData({
        title: postData.title,
        description: postData.description,
        images: postData.images || []
      });
    } catch (error) {
      console.error('Error loading post:', error);
        presentToast(t('edit_post.load_error'), 'danger');
      history.goBack();
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!authUser?.uid || !currentUser) {
      presentToast(t('edit_post.auth_error'), 'warning');
      return;
    }

    if (formData.title.length > maxTitleChars) {
      presentToast(t('edit_post.max_title_chars_error', { max: maxTitleChars }), 'warning');
      return;
    }

    if (formData.description.length > maxDescriptionChars) {
      presentToast(t('edit_post.max_description_chars_error', { max: maxDescriptionChars }), 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const postRef = doc(db, 'posts', id);
      // First get the current post data to preserve required fields
      const currentPost = await getDoc(postRef);
      if (!currentPost.exists()) {
        throw new Error('Post not found');
      }
      
      const currentData = currentPost.data();
      await updateDoc(postRef, {
        ...currentData,
        title: formData.title,
        description: formData.description,
        images: formData.images,
        updatedAt: Timestamp.now()
      });

      presentToast(t('edit_post.success'), 'success');
      history.goBack();
    } catch (error) {
      console.error('Error updating post:', error);
      presentToast(t('edit_post.error'), 'danger');
      setIsSubmitting(false);
    }
  };

  if (isPageLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/home" />
            </IonButtons>
            <IonTitle>{t('edit_post.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography>{t('common.loading')}</Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>{t('edit_post.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            value={formData.title}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              title: e.target.value
            }))}
            placeholder={t('edit_post.title_placeholder')}
            inputProps={{ maxLength: maxTitleChars }}
            helperText={`${formData.title.length}/${maxTitleChars}`}
            fullWidth
          />

          <TextField
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              description: e.target.value
            }))}
            placeholder={t('edit_post.description_placeholder')}
            inputProps={{ maxLength: maxDescriptionChars }}
            helperText={`${formData.description.length}/${maxDescriptionChars}`}
            fullWidth
          />

          {formData.images.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.images.map((url, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: 100,
                    height: 100
                  }}
                >
                  <img
                    src={url}
                    alt={t('edit_post.image_preview', { index: index + 1 })}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.title.trim() || !formData.description.trim() || isSubmitting}
            fullWidth
          >
            {t('edit_post.save')}
          </Button>
        </Box>
      </IonContent>
    </IonPage>
  );
};
